import React, { useState } from 'react';
import { ShareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const ShareButton = ({ url, title, description }) => {
    const [showMenu, setShowMenu] = useState(false);
    const shareUrl = url || window.location.href;
    const shareTitle = title || document.title;
    const shareText = description || '';

    // Web Share API (Mobil ve bazı modern tarayıcılarda)
    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    text: shareText,
                    url: shareUrl,
                });
                toast.success('Shared successfully!');
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                }
            }
        } else {
            setShowMenu(!showMenu);
        }
    };

    // Linki kopyala
    const copyToClipboard = () => {
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        setShowMenu(false);
    };

    // Sosyal medya paylaşım linkleri
    const shareLinks = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    };

    const socialButtons = [
        { name: 'WhatsApp', icon: '📱', color: 'bg-green-500 hover:bg-green-600', url: shareLinks.whatsapp },
        { name: 'Twitter', icon: '𝕏', color: 'bg-black hover:bg-gray-800', url: shareLinks.twitter },
        { name: 'Facebook', icon: 'f', color: 'bg-blue-600 hover:bg-blue-700', url: shareLinks.facebook },
        { name: 'LinkedIn', icon: 'in', color: 'bg-blue-700 hover:bg-blue-800', url: shareLinks.linkedin },
        { name: 'Telegram', icon: '✈️', color: 'bg-sky-500 hover:bg-sky-600', url: shareLinks.telegram },
    ];

    return (
        <div className="relative">
            <button
                onClick={handleNativeShare}
                className="flex items-center gap-2 px-4 py-2 bg-light-primary dark:bg-dark-primary text-white rounded-lg hover:opacity-90 transition-opacity"
                aria-label="Share this page"
            >
                <ShareIcon className="h-5 w-5" />
                <span>Share</span>
            </button>

            {/* Desktop share menu */}
            <AnimatePresence>
                {showMenu && !navigator.share && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowMenu(false)}
                        />
                        
                        {/* Share menu */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: -10 }}
                            className="absolute right-0 mt-2 w-64 bg-light-card dark:bg-dark-card rounded-lg shadow-xl border border-light-border dark:border-dark-border z-50"
                        >
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold text-light-text dark:text-dark-text">Share</h3>
                                    <button
                                        onClick={() => setShowMenu(false)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Copy link button */}
                                <button
                                    onClick={copyToClipboard}
                                    className="w-full mb-3 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    📋 Copy Link
                                </button>

                                {/* Social media buttons */}
                                <div className="grid grid-cols-5 gap-2">
                                    {socialButtons.map((social) => (
                                        <a
                                            key={social.name}
                                            href={social.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${social.color} text-white rounded-lg p-3 flex items-center justify-center font-bold text-lg transition-colors`}
                                            title={`Share on ${social.name}`}
                                            onClick={() => setShowMenu(false)}
                                        >
                                            {social.icon}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ShareButton;
